import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RtlIcon } from '@/components/ui/rtl-icon';

interface SettingsBreadcrumbProps {
  /**
   * The module name (translation key) - e.g., 'locations', 'team_members'
   */
  moduleName: string;
  /**
   * The route to navigate to when clicking the module name
   */
  moduleRoute: string;
  /**
   * Optional third breadcrumb item (e.g., 'create', 'edit')
   */
  action?: string;
}

/**
 * Standardized breadcrumb component for all Settings pages.
 *
 * Rules:
 * - "Settings" is NOT clickable (pure label)
 * - Module name IS clickable and navigates to list page
 * - Optional action (create/edit) is NOT clickable
 *
 * Examples:
 * - Settings > Locations
 * - Settings > Locations > Create
 * - Settings > Team Members > Edit
 */
export const SettingsBreadcrumb = ({
  moduleName,
  moduleRoute,
  action,
}: SettingsBreadcrumbProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <nav className='flex items-center gap-1.5 text-sm text-sd-muted-foreground'>
      {/* Settings - NOT clickable */}
      <span>{t('settings')}</span>
      {/* Directional icon: must mirror in RTL, or the crumb points back the
          way the merchant came. Fixed here rather than per page — all 13
          settings screens render through this component. */}
      <RtlIcon>
        <ChevronRight className='h-3.5 w-3.5' strokeWidth={1.5} />
      </RtlIcon>

      {/* Module Name - IS clickable */}
      <button
        onClick={() => navigate(moduleRoute)}
        className='text-sd-foreground font-medium hover:text-sd-primary transition-colors'
      >
        {t(moduleName)}
      </button>

      {/* Optional Action - NOT clickable */}
      {action && (
        <>
          <RtlIcon>
            <ChevronRight className='h-3.5 w-3.5' strokeWidth={1.5} />
          </RtlIcon>
          <span className='text-sd-foreground font-medium'>{t(action)}</span>
        </>
      )}
    </nav>
  );
};
